import { useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveCommunityLandingPath } from '@/lib/community-navigation';
import {
  buildMembershipForest,
  collectLeafIds,
  getForcedExpandedIdsInForest,
  resolveSmartSwitchTarget,
  type MembershipTreeNode,
  type SwitcherCommunity,
} from '@/lib/community-switcher-tree';
import { getLastLeafInGroup, recordLastLeafForAncestors } from '@/lib/last-active-community';
import type { UserWithCommunities } from '@/types';

interface CommunitySwitcherMenuProps {
  communities: SwitcherCommunity[];
  currentCommunityId: string;
  user: Pick<UserWithCommunities, 'id'> | null | undefined;
  userId?: string | null;
  onRequestClose?: () => void;
}

function NodeRow({
  node,
  depth,
  expandedIds,
  lockedExpandedIds,
  currentCommunityId,
  preferredLeafIds,
  onExpand,
  onCollapse,
  onNavigate,
}: {
  node: MembershipTreeNode;
  depth: number;
  expandedIds: Set<string>;
  lockedExpandedIds: Set<string>;
  currentCommunityId: string;
  preferredLeafIds: Set<string>;
  onExpand: (id: string) => void;
  onCollapse: (id: string) => void;
  onNavigate: (community: SwitcherCommunity) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.community.id);
  const isCollapseLocked = lockedExpandedIds.has(node.community.id);
  const isCurrent = currentCommunityId === node.community.id;
  const isAggregator = node.community.kind === 'AGGREGATOR';
  const isPreferredLeaf = preferredLeafIds.has(node.community.id);
  const isInvited = node.community.status === 'INVITED';

  const handleClick = (e: MouseEvent) => {
    e.preventDefault();
    const result = resolveSmartSwitchTarget(node, { isExpanded });
    if (result.action === 'expand') {
      onExpand(node.community.id);
      return;
    }
    onNavigate(node.community);
  };

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 w-full min-h-11 rounded-xl transition-colors border',
          isCurrent
            ? 'bg-primary/15 border-primary/40 shadow-sm ring-1 ring-primary/20'
            : isInvited
              ? 'border-amber-200 bg-amber-50/50 hover:bg-amber-50'
              : 'border-transparent text-foreground hover:bg-accent/5 hover:text-foreground'
        )}
        style={{ paddingLeft: 8 + depth * 14 }}
      >
        <button
          type="button"
          className={cn(
            'h-7 w-7 shrink-0 flex items-center justify-center rounded-md hover:bg-accent/10 disabled:opacity-40',
            isCurrent ? 'text-primary' : 'text-foreground'
          )}
          disabled={!hasChildren || (isExpanded && isCollapseLocked)}
          onClick={(e) => {
            e.stopPropagation();
            if (!hasChildren) return;
            if (isExpanded) {
              if (isCollapseLocked) return;
              onCollapse(node.community.id);
            } else {
              onExpand(node.community.id);
            }
          }}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown
                className={cn('h-3.5 w-3.5', isCurrent ? 'text-primary' : 'text-foreground')}
              />
            ) : (
              <ChevronRight
                className={cn('h-3.5 w-3.5', isCurrent ? 'text-primary' : 'text-foreground')}
              />
            )
          ) : (
            <span className="w-3.5" />
          )}
        </button>

        <button
          type="button"
          onClick={handleClick}
          className="flex items-center gap-2.5 flex-1 min-w-0 py-2 pr-2 text-left cursor-pointer"
        >
          <div
            className={cn(
              'h-8 w-8 rounded-lg flex items-center justify-center p-1 shrink-0',
              isCurrent
                ? 'bg-primary/20 text-primary'
                : isInvited
                  ? 'bg-amber-100 text-amber-600'
                  : 'bg-muted text-foreground'
            )}
          >
            {node.community.logo ? (
              <img
                src={node.community.logo}
                alt={node.community.name}
                className="h-full w-full object-contain"
              />
            ) : (
              <Building2
                className={cn(
                  'h-4 w-4',
                  isCurrent ? 'text-primary' : isInvited ? 'text-amber-600' : 'text-foreground'
                )}
              />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <span
              className={cn(
                'text-sm truncate block',
                isCurrent
                  ? 'font-bold text-primary'
                  : isInvited
                    ? 'font-semibold text-amber-700'
                    : 'font-medium text-foreground'
              )}
            >
              {node.community.name}
            </span>
            <div className="flex items-center gap-2">
              {isInvited && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
                  Invited
                </span>
              )}
              {isAggregator && (
                <span
                  className={cn(
                    'text-[10px] uppercase tracking-wide',
                    isCurrent ? 'text-primary/80' : 'text-muted-foreground'
                  )}
                >
                  Aggregator
                </span>
              )}
              {isPreferredLeaf && !isCurrent && (
                <span className="text-[10px] text-muted-foreground">Recent</span>
              )}
            </div>
          </div>
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <NodeRow
              key={child.community.id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              lockedExpandedIds={lockedExpandedIds}
              currentCommunityId={currentCommunityId}
              preferredLeafIds={preferredLeafIds}
              onExpand={onExpand}
              onCollapse={onCollapse}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function CommunitySwitcherMenu({
  communities,
  currentCommunityId,
  user,
  userId,
  onRequestClose,
}: CommunitySwitcherMenuProps) {
  const navigate = useNavigate();
  const forest = useMemo(() => buildMembershipForest(communities), [communities]);

  const lockedExpandedIds = useMemo(
    () => new Set(getForcedExpandedIdsInForest(forest, currentCommunityId)),
    [forest, currentCommunityId]
  );

  const [manualExpandedIds, setManualExpandedIds] = useState<Set<string>>(() => new Set());
  const [manualCollapsedIds, setManualCollapsedIds] = useState<Set<string>>(() => new Set());

  const expandedIds = useMemo(() => {
    const next = new Set(lockedExpandedIds);
    for (const id of manualExpandedIds) next.add(id);
    for (const id of manualCollapsedIds) {
      if (!lockedExpandedIds.has(id)) next.delete(id);
    }
    return next;
  }, [lockedExpandedIds, manualExpandedIds, manualCollapsedIds]);

  const byId = useMemo(() => {
    const map = new Map(communities.map((c) => [c.id, c]));
    return map;
  }, [communities]);

  const preferredLeafIds = useMemo(() => {
    const ids = new Set<string>();
    if (!userId) return ids;

    const current = byId.get(currentCommunityId);
    const isCurrentLeaf = Boolean(current && current.kind !== 'AGGREGATOR');

    for (const root of forest) {
      const walk = (node: MembershipTreeNode) => {
        if (node.community.kind === 'AGGREGATOR' || node.children.length > 0) {
          const leafIds = collectLeafIds(node);
          if (isCurrentLeaf && leafIds.includes(currentCommunityId)) {
            ids.add(currentCommunityId);
          } else {
            const last = getLastLeafInGroup(userId, node.community.id);
            if (last && leafIds.includes(last)) ids.add(last);
          }
        }
        node.children.forEach(walk);
      };
      walk(root);
    }
    return ids;
  }, [forest, userId, currentCommunityId, byId]);

  const onExpand = (id: string) => {
    setManualCollapsedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setManualExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const onCollapse = (id: string) => {
    if (lockedExpandedIds.has(id)) return;
    setManualExpandedIds((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setManualCollapsedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const onNavigate = (community: SwitcherCommunity) => {
    if (community.status === 'INVITED') {
      onRequestClose?.();
      navigate(`/${community.id}/invite`);
      return;
    }
    const full = byId.get(community.id) ?? community;
    if (userId && full.kind !== 'AGGREGATOR') {
      const membershipById = byId;
      const ancestorGroupIds: string[] = [];
      let parentId = full.parentCommunityId ?? null;
      const seen = new Set<string>();
      while (parentId && !seen.has(parentId)) {
        seen.add(parentId);
        ancestorGroupIds.push(parentId);
        parentId = membershipById.get(parentId)?.parentCommunityId ?? null;
      }
      if (ancestorGroupIds.length > 0) {
        recordLastLeafForAncestors(userId, full.id, ancestorGroupIds);
      }
    }
    onRequestClose?.();
    navigate(resolveCommunityLandingPath(full, user));
  };

  return (
    <div className="px-1 pb-1 space-y-0.5 max-h-[60vh] overflow-y-auto">
      {forest.map((node) => (
        <NodeRow
          key={node.community.id}
          node={node}
          depth={0}
          expandedIds={expandedIds}
          lockedExpandedIds={lockedExpandedIds}
          currentCommunityId={currentCommunityId}
          preferredLeafIds={preferredLeafIds}
          onExpand={onExpand}
          onCollapse={onCollapse}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}
